import React from 'react'

const L = ({ href, children }: { href: string; children: React.ReactNode }) => (
	<a
		href={href}
		className='underline decoration-stone-400 underline-offset-2 hover:opacity-70 transition-opacity'
	>
		{children}
	</a>
)

export const procedureContent: Record<string, React.ReactNode> = {
	'brow-lift': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery contains before and after images of patients who underwent a lateral temporal{' '}
				<L href='/plastic-surgery/brow-lift'>brow lift</L> in Cincinnati with Dr. Jeffrey Harmon.
				Dr. Harmon is a fellowship trained,{' '}
				<L href='/blog/doubledual-board-certified-facial-plastic-surgeon-what-does-it-mean-why-is-it-important'>
					double board-certified
				</L>{' '}
				facial plastic surgeon in Ohio. He specializes in brow lift surgery that precisely targets
				the complex three-dimensional changes that result in brow ptosis, lifting the eyebrows in a
				way that is natural-appearing.
			</p>

			<p>
				Brow aging is complex, but changes are often obvious and easily recognized by patients.{' '}
				<L href='/aesthetic-concerns/hooded-eyes'>
					The eyes can appear tired or even angry secondary to heaviness of the brow and the
					resultant crowding of the upper eyelid
				</L>
				. This is due to soft tissue hanging in the broader temple region. However, it is noticed
				most at the eyebrow and eye. Dr. Harmon believes this is because eyebrow hair is a
				well-defined, linear mark on the upper one-third of the face and the eyes are a focal point
				during conversation. Addressing these changes is key to effective, natural-appearing brow
				lift surgery.
			</p>

			<p>
				Patients curious about how a lateral temporal brow lift compares with other approaches,
				including a{' '}
				<L href='/blog/how-do-brow-lift-procedures-differ'>forehead lift</L> or an{' '}
				<L href='/blog/how-do-brow-lift-procedures-differ'>endoscopic brow lift</L>, can read a
				full comparison on Dr. Harmon&apos;s blog.
			</p>

			<p>
				In contrast, Dr. Harmon feels the lateral temporal brow lift, which can be referred to as
				simply a brow lift or temple reset, best addresses the signs of aging in this location in a
				natural-appearing, balanced way. The gallery demonstrates a range of effects of the lateral
				temporal brow lift, reflecting individuals with unique anatomy. The overall effect is not
				necessarily a strong declaration of how high the brow can be lifted, which, if too high, can
				make the face look perpetually surprised, especially at the medial portion of the eyebrow.
				Instead, the effect is that the eyes can appear more open and brighter without disrupting
				the natural arch of the eyebrow hair. The transition from brow to upper eyelid demonstrates
				less crowding at the outer corner of the eye. The position of the hairline is unchanged from
				the before images in every case, a visual marker of how limited the incisions for this
				approach actually are.
			</p>

			<p>
				As suggested above, the lateral brow is intimately associated with the upper eyelid. In
				fact, a lateral temporal brow lift is often performed with an{' '}
				<L href='/plastic-surgery/blepharoplasty'>upper blepharoplasty</L>. This is because, when
				the lateral brow hangs down, it hangs over the lateral eyelid. This results in an overlap of
				excess tissue at this location, which can worsen the appearance of excess upper eyelid skin.
				A lateral temporal brow lift can address an area around the eyes the upper blepharoplasty
				cannot and vice versa. This is why Dr. Harmon closely evaluates each patient to determine
				whether one or both anatomic areas are contributing to the feature(s) that bother them
				aesthetically.
			</p>
		</div>
	),

	'deep-plane-facelift': (
		<div className='flex flex-col gap-5'>
			<p>
				Patients who undergo{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>deep plane facelift surgery</L> with
				Cincinnati facial plastic surgeon Dr. Jeffrey Harmon seek natural-appearing, balanced
				facelift results that address one or more areas of specific concern on their face. These
				concerns can include:
			</p>

			<ol className='list-decimal pl-6 flex flex-col gap-3'>
				<li>
					Sagging volume – composed of skin, fat, and the platysma muscle – superiorly around the
					mouth and inferiorly at the jawline. This feature is referred to as{' '}
					<L href='/aesthetic-concerns/jowls'>jowls</L>.
				</li>
				<li>
					Drooping volume – composed of skin and fat compartments – in the cheeks that fall away
					from the lower eyelids and hang over the upper lip. This feature is referred to as cheek
					or midface ptosis.
				</li>
				<li>
					Redundant, loose tissue – composed of skin, fat, and the platysma muscle – immediately
					under the jawline in an area called the submentum that can contribute to a{' '}
					<L href='/aesthetic-concerns/double-chin'>double chin</L>. This feature is also referred
					to as platysmal banding.
				</li>
			</ol>

			<p>
				The overall effect of these changes is a face that can appear from a front view more boxy
				and bottom-heavy rather than heart-shaped and tapered. The jawline can appear shorter from a
				side view, because jowls cut off a smooth transition from the chin to the back of the jaw
				(gonial angle). The jawline can even blend in with the neck in some individuals. These
				changes together reflect our age. They are often difficult for patients to describe. However,
				their concerns usually become clear in consultation when they point to these areas of the
				face and describe feeling like things are generally &quot;loose&quot; and
				&quot;sagging.&quot;
			</p>

			<p>
				Dr. Harmon is a{' '}
				<L href='/blog/doubledual-board-certified-facial-plastic-surgeon-what-does-it-mean-why-is-it-important'>
					double board-certified
				</L>{' '}
				facial plastic surgeon. He has a deep understanding of the extended deep plane facelift&apos;s{' '}
				<L href='/blog/what-is-the-difference-between-a-deep-plane-facelift-and-an-extended-deep-plane-facelift'>
					development
				</L>
				, its{' '}
				<L href='/blog/improving-extended-deep-plane-facelift'>continued refinement</L>, and the
				broader{' '}
				<L href='/blog/history-of-facelifts'>history of facelift surgery</L>. This understanding
				informs his approach to facelift surgery.
			</p>

			<p>
				Dr. Harmon feels that results from extended deep plane facelift surgery can be more
				comprehensive, natural-appearing, and balanced than older approaches, not only at rest but
				also with movement of the face, which he has observed can appear &quot;happier&quot; and
				more relaxed in many individuals after surgery. The patient Dr. Harmon meets in consultation
				usually do not seek to look like someone else. They also generally do not seek to look many
				decades younger. Instead, they describe wanting to look &quot;refreshed,&quot;
				&quot;natural,&quot; and &quot;not pulled.&quot; Dr. Harmon feels his approach to facelift
				surgery is the best way to achieve these desires.
			</p>

			<p>
				Dr. Harmon performs exclusively deep plane facelift surgery not only because of his
				specialized training but also because he believes the results can be superior to other
				approaches. His goals for deep plane facelift surgery include:
			</p>

			<ul className='list-disc pl-6 flex flex-col gap-3'>
				<li>
					Lifted cheeks as reflected in a gentler taper of volume from its border with the upper lip
					(melolabial folds) superior and lateral to the cheekbone prominence under the eyes.
				</li>
				<li>
					A more defined jawline from the chin to the angle of the jaw that also includes the border
					between the lower cheeks and the jawline (marionette lines).
				</li>
				<li>
					Skin and muscle that are lifted against the area under the jawline (submentum).
				</li>
			</ul>

			<p>
				The gallery contains before and after images that reflect some of his results. These images
				demonstrate a range of individuals with highly variable anatomic features whose personal
				goals were met and/or exceeded by Dr. Harmon, but who still look like themselves.
			</p>
		</div>
	),

	'neck-lift': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery contains before and after images of patients who underwent a{' '}
				<L href='/plastic-surgery/neck-lift'>neck lift</L> in Cincinnati with Dr. Jeffrey Harmon.
				Dr. Harmon is not only a neck lift surgeon, but is also a fellowship trained,{' '}
				<L href='/blog/doubledual-board-certified-facial-plastic-surgeon-what-does-it-mean-why-is-it-important'>
					double board-certified
				</L>{' '}
				facial plastic surgeon in Cincinnati with years of experience in independent practice and
				expertise in procedures involving the face, eyes, nose, and hair, in addition to the neck.
			</p>

			<p>
				A deep neck lift, also referred to as a platysmaplasty, is a neck contouring procedure that
				can help address the complex, multi-layered, three-dimensional changes in the neck that
				occur with aging. These changes involve, primarily, weakening and falling of the platysma
				muscle (neck bands). Candidates for neck lift surgery include those patients who experience
				these changes in the neck, which can blunt its natural angles and distort its natural
				contours.{' '}
				<L href='/blog/youthful-neck-anatomy-and-neck-lift-surgery'>
					The procedure can treat neck bands extending the entire length of the neck from the chin
					to the clavicles, creating sharper angles and better-defined contours to this area
				</L>
				.
			</p>

			<p>
				Patients weighing neck liposuction against a full neck lift can find a direct comparison of
				the two approaches on Dr. Harmon&apos;s blog,{' '}
				<L href='/blog/neck-liposuction-versus-a-neck-lift-which-procedure-is-the-most-appropriate-for-me'>
					Neck Liposuction Versus a Neck Lift: Which Procedure is the Most Appropriate for Me?
				</L>
			</p>

			<p>
				The gallery reflects the above-described changes a neck lift can achieve. And, as is evident
				in the gallery, a neck lift is usually, though not exclusively, performed with a{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>deep plane facelift</L>. It is
				important to note that a neck lift can be performed in isolation. The same incision
				immediately under the chin is placed to address the neck centrally. However, unlike a deep
				plane facelift, the portion of the incision around the ear largely falls behind the ear
				rather than both in front and behind the ear when a facelift and neck lift are performed
				together. Balanced, natural-appearing results can be achieved with a neck lift in
				appropriate patients. A deep plane facelift enhances the results by addressing what a neck
				lift only cannot, which includes the jowls at the jawline and a small portion of the tissue
				around the jawline and immediately under it in the area called the submentum.
			</p>

			<p>
				The recovery timeline varies by individual and is due to a host of factors including, but
				not limited to, patient medical history, surgical history, anatomy, and patient perception.
				However, it is usually a similar recovery to a combined deep plane facelift and neck lift,
				which can be broken up into the days, weeks, and months after surgery. Healing tends to be
				progressive, quicker earlier in the recovery, and vary slightly each day, with some days
				feeling better and others worse. Dr. Harmon has observed that patients usually feel
				comfortable going out within two (2) to three (3) weeks, though they cannot hide every
				aspect of their procedure. Swelling improves over the ensuing weeks and months and final
				healing can take one year or more, though patients find the signs they have had surgery
				(e.g. incisions) tend to fade much sooner.
			</p>
		</div>
	),

	blepharoplasty: (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery contains before and after images of patients who underwent{' '}
				<L href='/plastic-surgery/blepharoplasty'>eyelid surgery</L> in Cincinnati with fellowship
				trained, double board-certified facial plastic surgeon Dr. Jeffrey Harmon. Patients
				interested in pursuing an eyelid lift in Ohio seek Dr. Harmon for his training, experience,
				and expertise treating both the upper eyelids and lower eyelids. Patients&apos; frequently
				stated goals for these procedures are to look &quot;more rested&quot; with &quot;more open
				eyes&quot; but without looking &quot;operated on.&quot; What this means, clinically, depends
				on the location(s) treated. It typically involves reducing excess folds of skin and reducing
				puffiness.
			</p>

			<p>
				There are examples in the gallery of both upper blepharoplasty and lower blepharoplasty in
				Cincinnati by Dr. Harmon. And while both procedures target the eyelids, upper eyelid surgery
				and lower eyelid surgery have different goals that reflect the unique anatomy of each
				location. Dr. Harmon&apos;s surgical emphasis is on reducing excess skin and preserving and
				reorienting fat in a functionally safe and aesthetically elegant way to achieve balanced,
				natural-appearing results.
			</p>

			<p>
				Heaviness and drooping of the lateral eyebrow can worsen the appearance of the upper eyelid
				at its most lateral extent. This change is best addressed with a{' '}
				<L href='/plastic-surgery/brow-lift'>brow lift</L>. Changes to the skin surface such as
				fine lines and sun damage (solar lentigines) contribute to the appearance of aging in this
				location. These features are best treated with{' '}
				<L href='/non-surgical/laser-resurfacing'>resurfacing procedures</L> such as a laser or
				chemical peel.
			</p>

			<p>
				A more focused procedure called a{' '}
				<L href='/blog/what-is-a-lower-eyelid-skin-pinch-and-what-can-it-do-for-me'>
					lower eyelid skin pinch
				</L>{' '}
				may be worth discussing as an alternative to full lower blepharoplasty for patients whose
				primary concern is limited to excess lower eyelid skin near the margin of the eyelashes.
			</p>
		</div>
	),

	'laser-resurfacing': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery demonstrates before and after images of patients who underwent{' '}
				<L href='/non-surgical/laser-resurfacing'>laser resurfacing</L> in Cincinnati with Dr.
				Jeffrey Harmon. Dr. Harmon is not only a facial resurfacing surgeon but also a fellowship
				trained, double board-certified facial plastic surgeon who specializes in the surgical and
				non-surgical care of the entire face, eyes, nose, neck, and hair.
			</p>

			<p>
				Readers interested in the technical details of how ablative fractional CO2 lasers work can
				find an <L href='/blog/cosmetic-laser-treatments-an-explainer'>explainer</L> on Dr.
				Harmon&apos;s blog.
			</p>

			<p>Laser resurfacing can improve the following features of the skin:</p>

			<ul className='list-disc pl-6 flex flex-col gap-2'>
				<li>Textural abnormalities (e.g. large pores)</li>
				<li>Pigmentation from sun damage (e.g. solar lentigines)</li>
				<li>
					<L href='/aesthetic-concerns/wrinkles'>Fine lines</L>
				</li>
				<li>Mild-to-moderate acne scarring</li>
			</ul>

			<p>
				The gallery demonstrates improvements in these features. However, it is important to
				understand that this treatment is not an &quot;eraser&quot; for the face. One consideration
				of surgeons during laser skin resurfacing is to balance effectiveness with safety to minimize
				risks. Dr. Harmon has identified what he feels is an appropriate balance for active patients
				who want an effective treatment, reduced risk of complications, and reasonable recovery for
				skin resurfacing in Ohio.
			</p>

			<p>
				<L href='/blog/skin-care-after-laser-resurfacing-of-the-face'>Recovery</L> is typically
				approximately seven (7) to ten (10) days. Redness and swelling usually last a few days.
				White spots i.e. the treatment sites sometimes turn brown and gradually disappear as the
				face is washed and moisturizer re-applied. Patients are instructed to be careful in the sun
				for the next approximately three (3) months. They are instructed to apply mineral-based,
				broad-spectrum sunscreen and to wear a hat and sunglasses to reduce the risk of prolonged
				redness in certain areas after treatment. Of note, recovery varies from person to person and
				may differ from the estimates above.
			</p>

			<p>
				Good candidates are patients who demonstrate the above-described changes to the skin. The
				fractional ablative CO2 laser is generally not appropriate for individuals with darker skin
				tones due to the increased risk of lightening or darkening of the skin associated with this
				treatment in these individuals. Instead, there are other treatments available for a wider
				range of skin tones.
			</p>

			<p>
				Dr. Harmon frequently pairs laser skin resurfacing with treatments that are sometimes
				labelled as regenerative medicine. These treatments include nano-fat with or without
				platelet rich plasma (PRP) injection{' '}
				<L href='/blog/addressing-smokers-lines-peri-oral-rhytids'>around the mouth</L> (smoker&apos;s
				lines) and/or the lower eyelids to enhance healing and the overall effects of the
				resurfacing procedure.
			</p>

			<p>
				These procedures,{' '}
				<L href='/blog/skin-improvement-versus-structural-facial-aging'>
					while they are intended to treat skin surface level changes, complement surgical procedures
					that address structural changes to the face well
				</L>
				. In fact, they can be integrated into a more comprehensive facial rejuvenation treatment
				plan that involves surgical procedures such as a{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>deep plane facelift</L>,{' '}
				<L href='/plastic-surgery/neck-lift'>neck lift</L>,{' '}
				<L href='/plastic-surgery/brow-lift'>brow lift</L>,{' '}
				<L href='/plastic-surgery/lip-lift'>lip lift</L>, and{' '}
				<L href='/plastic-surgery/blepharoplasty'>blepharoplasty</L>. The incisions from many of
				these procedures can even be treated with the laser to accelerate their healing.
			</p>
		</div>
	),

	'hair-restoration': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery demonstrates before and after images of patients who underwent{' '}
				<L href='/hair/hair-restoration'>hair transplant</L> in Cincinnati with Dr. Jeffrey Harmon.
				Dr. Harmon is not only a specialist in hair restoration in Cincinnati, but he is also a
				fellowship trained, double board-certified facial plastic surgeon who is an experienced
				expert in the surgical and non-surgical care of the face, eyes, nose, and neck. Moreover,
				he is not only a surgical but also a{' '}
				<L href='/hair/hair-loss-treatment'>non-surgical hair loss treatment</L> facial surgeon.
			</p>

			<p>Hair follicles are placed in areas of hair loss to address one or more of the following conditions:</p>

			<ul className='list-disc pl-6 flex flex-col gap-2'>
				<li>Receding hairline.</li>
				<li>Thinning at the crown, the circular portion at the top of the scalp&apos;s most posterior portion.</li>
				<li>Diffuse hair loss from the hairline to the crown.</li>
			</ul>

			<p>
				The before and after results in the gallery demonstrate patients who have hairline recession
				and moderate thinning on their scalp as well as diffuse, severe hair loss. Also, a range of
				hair colors is demonstrated, which can affect the difficulty of achieving good aesthetic
				results. The before and after images demonstrate a post-operatively denser hairline where
				hairline-defining landmarks are appropriately shaped and the distribution of hair follicles
				is balanced and natural appearing. The hairlines have irregularity, with density that varies
				slightly as well as an overall path that varies slightly across the scalp. This is where the
				artistry of hair restoration surgery factors in. Dr. Harmon anticipates how the hair will
				grow after placement to maximize aesthetic results.
			</p>

			<p>
				A good candidate for hair restoration surgery usually has significant hair loss in their
				area(s) of concern. However, too much hair loss may result in too insignificant an effect
				from surgery, since relatively large numbers of hair follicles are necessary to achieve
				adequate density when there is significant thinning. A good candidate also has adequate hair
				density in the area(s) of the scalp where hair follicles will be removed, reasonable
				expectations about what can be achieved, an appropriately defined hair loss pattern, and no
				medical or surgical contraindications to surgery. It is important to note that a
				natural-appearing hairline still reflects an individual&apos;s age. There is likely
				inadequate hair to bring a hairline forward to where it was in youth. And unfortunately, not
				every patient is a candidate for hair restoration surgery.{' '}
				<L href='/blog/what-medical-conditions-leading-to-hair-loss-would-not-be-appropriate-for-hair-restoration-surgery'>
					Certain medical conditions
				</L>{' '}
				may make someone unsuitable for the procedure. Performing hair restoration surgery on a
				patient in their early 20s, for example, could appear unnatural over time, because their
				final hair distribution pattern has not yet emerged and could result in significant hair loss
				behind the previously transferred hair follicles.
			</p>

			<p>
				Hair tends to be shed shortly after surgery then the transferred hair follicles begin to
				produce more hair. Dr. Harmon{' '}
				<L href='/blog/hair-loss-treatments-an-explainer'>
					has found that non-surgical interventions including medication and platelet-rich-plasma can
					help reduce shedding and accelerate hair shaft growth after surgery
				</L>
				. It generally takes nine (9) to eighteen (18) months for the results of hair restoration
				surgery, though the benefits are typically enjoyed much sooner as the newly transferred hair
				begins to grow.
			</p>
		</div>
	),

	rhinoplasty: (
		<div className='flex flex-col gap-5'>
			<p>
				Facial plastic surgeon Dr. Jeffrey Harmon treats the full range of patients who are
				potential candidates for{' '}
				<L href='/plastic-surgery/rhinoplasty'>rhinoplasty surgery (nose job)</L> in Cincinnati.
				This includes individuals who have never undergone surgery and those who have undergone one
				or more previous procedures and are seeking revision rhinoplasty. These patients{' '}
				<L href='/blog/what-is-the-difference-between-a-functional-and-a-cosmetic-rhinoplasty'>
					may be seeking changes to the shape i.e. the aesthetics of their nose, improvements in
					the function of their nose, or both
				</L>
				.
			</p>

			<p>
				The gallery of before and after images demonstrates patients who underwent their first
				rhinoplasty surgery. The gallery also includes patients who have undergone surgery elsewhere
				and sought a rhinoplasty surgeon in Ohio who could address complex aesthetic and/or
				functional concerns from their previous operation. Dr. Harmon also performs rhinoplasty
				surgery that addresses functional concerns only, though this is inadequately reflected in
				images and so are not included in this gallery. However, these images do reflect Dr.
				Harmon&apos;s general approach to rhinoplasty surgery, which avoids trends or
				&quot;looks&quot; and seeks to harmonize the nose with the rest of the face.
			</p>

			<p>
				These images demonstrate results that are{' '}
				<L href='/blog/what-concerns-can-be-addressed-with-nose-surgery'>
					significant and obvious
				</L>{' '}
				but that are also proportional and balanced. As stated previously, these results do not
				demonstrate a singular or signature &quot;look.&quot; Instead, these results reflect each
				patient&apos;s anatomy balanced with their aesthetic desires.
			</p>

			<p>
				Achieving this balance is what makes rhinoplasty surgery so complex. The gallery reflects
				the care <L href='/about-us/the-harmon-method'>Dr. Harmon</L> takes with each patient to
				achieve their goals, navigating the complexities of rhinoplasty surgery with focused
				attention and care.
			</p>
		</div>
	),

	'chin-implants': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery demonstrates before and after images of patients who underwent{' '}
				<L href='/plastic-surgery/chin-implants'>chin implant</L> in Cincinnati with Dr. Jeffrey
				Harmon to <L href='/blog/treatment-options-for-a-weak-chin'>treat a weak chin</L>. Dr.
				Harmon is not only an expert at chin implant surgery, but he is also a fellowship trained,
				double board-certified facial plastic surgeon in Cincinnati who specializes in the surgical
				and non-surgical care of the entire face, eyes, nose, neck, and hair.
			</p>

			<p>
				Often confused with a{' '}
				<L href='/aesthetic-concerns/double-chin'>double chin</L>, a weak chin secondary to poor
				chin projection can adversely affect facial balance, especially as it relates to the nose,
				neck, and jawline. The nose can appear more projected than it is. It is essential to
				consider this feature when evaluating patients for rhinoplasty. Otherwise, the nose may be
				unnecessarily de-projected. A chin implant can be placed at the same time as a rhinoplasty
				procedure to improve balance in this area while addressing aesthetic concerns related to the
				nose. The neck can appear less defined and the angle between the jawline and neck more
				blunted with a weak chin. This can make the neck appear more aged. Finally, a shorter
				jawline secondary to a weak chin can make the face appear aged. In summary, poor facial
				balance with the chin can make the nose look larger and the face and neck look older.
			</p>

			<p>
				As is evident in the gallery, the most dramatic changes associated with chin implant surgery
				are from a side view. The chin is more projected. It lies closer to a vertical line dropped
				from the lower lip in profile. However, as is evident on three-quarters view, improved
				fullness of the jawline that tapers off from the center can be achieved using a style of
				chin implant Dr. Harmon prefers.
			</p>
		</div>
	),

	fillers: (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery demonstrates before and after images of{' '}
				<L href='/non-surgical/fillers'>fillers</L> in Cincinnati with fellowship trained, double
				board-certified facial plastic surgeon Dr. Jeffrey Harmon. Dr. Harmon is an expert in
				surgical care of the face, eyes, nose, hair, and neck. He feels that the same focus and
				care he gives to his surgical patients should also be given to his non-surgical patients. To
				that end, he performs all non-surgical facial rejuvenation in his Cincinnati office himself.
				The gallery demonstrates natural-appearing, balanced results that reflect Dr. Harmon&apos;s
				experience, skill, and ability to choose patients for whom this procedure is appropriate.
			</p>

			<p>Hyaluronic acid fillers may be an appropriate option for a subset of patients interested in:</p>

			<ul className='list-disc pl-6 flex flex-col gap-2'>
				<li>
					<L href='/aesthetic-concerns/hollow-face'>
						Volume restoration in, for example, hollowed cheeks and hollowed temples
					</L>{' '}
					as an alternative to{' '}
					<L href='/plastic-surgery/facial-fat-grafting'>facial fat grafting</L>.
				</li>
				<li>
					<L href='/blog/lip-lift-versus-lip-filler'>
						Improving lip volume and definition
					</L>
					.
				</li>
				<li>
					<L href='/blog/tear-trough-filler-an-explainer'>
						Softening of the under eye (tear troughs)
					</L>
					.
				</li>
				<li>
					<L href='/blog/what-can-a-liquid-rhinoplasty-achieve'>
						Softening of a sharp bump on the nasal bridge
					</L>{' '}
					(dorsal hump).
				</li>
				<li>
					<L href='/blog/treatment-options-for-a-weak-chin'>
						A trial to approximate what a chin implant may look like prior to considering surgical
					</L>{' '}
					placement of a <L href='/plastic-surgery/chin-implants'>chin implant</L>.
				</li>
				<li>
					<L href='/blog/now-offering-fda-approved-juvederm-skinvive-treatment-to-smooth-and-hydrate-cheek-skin'>
						Improving skin hydration in the cheeks
					</L>
					.
				</li>
			</ul>

			<p>
				It is important to understand that fillers are simply a tool{' '}
				<L href='/blog/are-lip-fillers-permanent'>
					whose effects tend to last on the order of months to two years rather than decades
				</L>
				. And while{' '}
				<L href='/blog/what-qualities-distinguish-hyaluronic-acid-dermal-filler-products'>
					there are a wide range of products available with different qualities appropriate for
					different areas of the face
				</L>
				, the education, training, and experience of the injector matters more than product
				selection. Dr. Harmon feels{' '}
				<L href='/blog/filler-fatigue'>
					patients should seek a provider to place dermal fillers in Ohio who offers both surgical
					and non-surgical treatment options
				</L>{' '}
				and who is willing to counsel patients appropriately when the desired location of filler,{' '}
				<L href='/blog/when-is-jawline-filler-not-aesthetically-appropriate'>
					for example, the jawline
				</L>
				, or the total amount desired could be harmful to aesthetics or simply unsafe. Furthermore,
				Dr. Harmon believes surgeon providers are more likely to understand anatomy better and,
				therefore,{' '}
				<L href='/blog/how-i-eliminate-or-significantly-reduce-pain-when-injecting-dermal-filler'>
					be more comfortable with specialized approaches to numbing specific sites on the face that
					can make placement of filler more comfortable
				</L>
				. Finally, a facial plastic surgeon can place fillers with an eye on the future as they
				better understand{' '}
				<L href='/blog/how-could-non-surgical-procedures-affect-my-future-facelift-and-neck-lift-surgery'>
					how non-surgical procedures can affect future surgical procedures that address deeper
					structural changes in the face associated with aging
				</L>
				.
			</p>

			<p>
				Another potential benefit of having a facial plastic surgeon such as Dr. Jeffrey Harmon
				placing filler is that treatment can be integrated into a broader aesthetic approach that
				includes surgery. After all,{' '}
				<L href='/blog/myth-versus-reality-does-a-non-surgical-facelift-or-neck-lift-exist'>
					a non-surgical facelift and/or neck lift does not actually exist
				</L>
				. For example, lip fillers can complement the effects of a{' '}
				<L href='/plastic-surgery/lip-lift'>lip lift</L> surgical procedure, with less volume often
				required to achieve aesthetic goals after surgery. Hyaluronic acid filler in the cheeks can
				complement a cheek lift achieved with{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>deep plane facelift surgery</L>.
				Hyaluronic acid filler in the temples can complement a lift of the lateral brow achieved
				with a lateral temporal{' '}
				<L href='/plastic-surgery/brow-lift'>brow lift</L>. These are a few examples of the
				possibilities fillers provide to patients as only one component of their aesthetic treatment
				plan.
			</p>
		</div>
	),

	'lip-lift': (
		<div className='flex flex-col gap-5'>
			<p>
				The lip lift before and after gallery demonstrates diverse results from a surgical{' '}
				<L href='/plastic-surgery/lip-lift'>lip lift</L> in Cincinnati with Dr. Jeffrey Harmon. Dr.
				Harmon also performs other facial plastic surgery procedures in Cincinnati. As demonstrated
				in the images, a lip lift can change multiple features of the three-dimensional structure of
				the upper lip. These changes include:
			</p>

			<ol className='list-decimal pl-6 flex flex-col gap-3'>
				<li>
					Reducing the distance between the base of the nose and the border between the red upper
					lip and lip skin, called the vermillion border. This is the most obvious change associated
					with lip lift surgery, because it addresses the most obvious change from facial aging,
					which is thinning and lengthening of the upper lip.
				</li>
				<li>
					Increasing the height of the red portion of the lip, which accentuates the natural curves
					of the red lip, including the cupid&apos;s peaks, that give the upper lip an m-shape.
				</li>
				<li>
					Improving fullness, also referred to as &quot;pout,&quot; of the red lip elegantly and
					without looking &quot;duck-lipped&quot; by rolling out additional red lip up and forward.
				</li>
				<li>
					Extending the continuity of the visible portion of the red lip laterally towards the edges
					of the mouth.
				</li>
			</ol>

			<p>
				The first three changes can be achieved with a subnasal lip lift. The fourth change can be
				achieved with a corner lip lift. Dr. Harmon evaluates each patient&apos;s unique anatomy to
				determine whether one or both procedures could be most beneficial.
			</p>

			<p>
				The lip lift contrasts with lip filler, which temporarily adds volume to the red portion of
				the upper lip only where it is placed. While lip filler cannot achieve comprehensive,
				long-lasting results like a surgical lip lift, some patients benefit from additional volume
				to the upper and lower lip after surgery. Dr. Harmon has the experience and expertise to
				determine whether the addition of filler to the lips after lip lift surgery can enhance or
				complement the surgical results.
			</p>

			<p>
				<L href='/blog/lip-lift-under-local-anesthesia'>
					A lip lift, both subnasal and corner, can be performed under local anesthesia
				</L>
				. A lip lift also complements other surgical procedures well. For example, the above four
				improvements contribute to the harmony of the face, especially when combined with the
				extended{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>deep plane facelift</L>,{' '}
				<L href='/plastic-surgery/neck-lift'>neck lift</L>, and/or lateral temporal{' '}
				<L href='/plastic-surgery/brow-lift'>brow lift</L>. This is because these procedures can
				lift drooping tissue across the face, shifting its three-dimensional structure to a more
				vertical, tapered, heart-shaped appearance. Similarly, a lip lift can shift the focus of
				the face more vertically with the surrounding facial features.
			</p>

			<p>
				Though the changes best addressed with a lip lift are typically seen with aging, this
				procedure does not exclusively benefit such individuals. In fact, some people have long upper
				lips from a relatively young age. Others have thin red upper lips and adequate upper lip
				skin for lip lift surgery. Still others have significant asymmetries in the shape of their
				red upper lip that can be improved through lip lift surgery. In other words, there are many
				individuals whose age ranges widely who could benefit from lip lift surgery to improve
				facial balance. The gallery demonstrates this.
			</p>
		</div>
	),

	'fat-grafting': (
		<div className='flex flex-col gap-5'>
			<p>
				The gallery contains before and after images of patients who underwent{' '}
				<L href='/plastic-surgery/facial-fat-grafting'>facial fat grafting</L> in Cincinnati with
				Dr. Jeffrey Harmon. Dr. Harmon is a fellowship trained, double board-certified facial
				plastic surgeon who specializes not only in facial volume restoration but in cosmetic
				surgical and non-surgical procedures involving the face, eyes, nose, neck, and hair.
			</p>

			<p>
				The purpose of fat grafting, also known as fat transfer, to the face is to increase the
				volume of the area in which the fat is placed. Fat grafting can help restore some of the
				volume and, therefore, contours patients had when they were younger or enhance their natural
				contours regardless of whether they had it in the past.{' '}
				<L href='/blog/what-are-common-areas-to-receive-fat-augmentation-in-the-face'>
					The patient&apos;s own fat is removed from the abdomen, inner thighs, or outer thighs and
					placed in the target area(s), which can include
				</L>
				:
			</p>

			<ul className='list-disc pl-6 flex flex-col gap-2'>
				<li>Cheeks</li>
				<li>Temples</li>
			</ul>

			<p>
				In contrast with facial fat grafting, hyaluronic acid filler is an exogenous, manufactured
				material modelled after human hyaluronic acid that serves as a relatively short-lived
				solution to volume loss when compared to relatively long-lasting fat. Sometimes, fillers are
				used to confirm that augmentation of the volume of the cheeks or temples achieves a
				patient&apos;s aesthetic goals. Then, fat grafting is used. Of note, some amount of fat is
				expected to be absorbed with any placement. And, while the fat that acquires a good blood
				supply and becomes integrated with the surrounding tissue is expected to last, the passage
				of time makes precisely how much fat remains impossible to know. But the fact remains that
				fat from the patient&apos;s own body can integrate and blend in with the surrounding tissue
				in a way that filler cannot.
			</p>

			<p>
				Facial fat grafting can be one component of a broader plan for facial rejuvenation in
				Cincinnati with Dr. Harmon, as the gallery demonstrates. For example, fat is frequently
				transferred to the face during{' '}
				<L href='/plastic-surgery/facelift/deep-plane-facelift'>facelift surgery</L>. Facelift{' '}
				<L href='/blog/what-are-common-areas-to-receive-fat-augmentation-in-the-face'>
					fat grafting
				</L>
				, as performed by Dr. Harmon, involves relatively small volumes of fat. This is because fat
				grafting is simply meant to enhance volume lifted in the cheeks rather than serve as a
				replacement for a facelift. Dr. Harmon feels this is the best way to achieve balanced,
				natural-appearing results.
			</p>
		</div>
	),
}
